import { ArrowRight, MessageCircle, ShieldCheck, TrendingUp, Sparkles } from "lucide-react";
import { COMPANY } from "../lib/constants";

const HERO_BG =
  "https://images.unsplash.com/photo-1470075801209-17f9ec0cada6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwyfHxnbGFzcyUyMGNvcnBvcmF0ZSUyMGJ1aWxkaW5nJTIwZmFjYWRlfGVufDB8fHx8MTc3OTg4ODg3MHww&ixlib=rb-4.1.0&q=85";

export default function Hero() {
  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden"
    >
      {/* Background image */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${HERO_BG})` }}
        aria-hidden="true"
      />
      <div className="absolute inset-0 hero-overlay" aria-hidden="true" />

      {/* Decorative grid lines */}
      <div
        className="absolute inset-0 opacity-[0.06] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",
          backgroundSize: "80px 80px",
        }}
        aria-hidden="true"
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 lg:py-40 w-full">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 text-white">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur border border-white/20 mb-8">
              <Sparkles className="w-4 h-4 text-[#00C7A5]" />
              <span className="text-xs font-semibold uppercase tracking-widest">
                Consultoria Contábil Premium
              </span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight">
              Contabilidade <span className="text-[#00C7A5]">Estratégica</span>
              <br />
              para empresas que querem <span className="text-[#00C7A5]">crescer</span>.
            </h1>

            <p className="mt-6 text-lg sm:text-xl text-white/85 max-w-2xl leading-relaxed font-light">
              Soluções contábeis completas com atendimento humanizado, agilidade e segurança para a sua empresa.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <button
                data-testid="hero-cta-primary"
                onClick={() => scrollTo("contatos")}
                className="group inline-flex items-center justify-center gap-2 bg-[#00C7A5] hover:bg-[#00A88A] text-white font-semibold uppercase tracking-wide px-8 py-4 rounded-full shadow-[0_12px_30px_rgba(0,199,165,0.4)] hover:-translate-y-1 transition-all"
              >
                Fale com um Especialista
                <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
              </button>

              <a
                data-testid="hero-cta-whatsapp"
                href={COMPANY.whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/15 backdrop-blur border border-white/30 text-white font-semibold uppercase tracking-wide px-8 py-4 rounded-full transition-all hover:-translate-y-1"
              >
                <MessageCircle className="w-5 h-5" />
                WhatsApp
              </a>
            </div>

            {/* Trust pills */}
            <div className="mt-12 grid grid-cols-2 sm:grid-cols-3 gap-4 max-w-xl">
              {[
                { icon: ShieldCheck, label: "Segurança total" },
                { icon: TrendingUp, label: "Estratégia fiscal" },
                { icon: Sparkles, label: "Atendimento premium" },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-2 text-white/85 text-sm">
                  <item.icon className="w-5 h-5 text-[#00C7A5] flex-shrink-0" />
                  <span className="font-medium">{item.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right floating card */}
          <div className="lg:col-span-5 hidden lg:block">
            <div className="relative">
              <div className="absolute -inset-4 bg-[#00C7A5]/20 rounded-3xl blur-3xl" aria-hidden="true" />
              <div className="relative bg-white/95 backdrop-blur rounded-2xl p-8 shadow-2xl animate-float">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <p className="text-xs uppercase tracking-widest text-[#4B5563] font-semibold">
                      Indicadores
                    </p>
                    <p className="text-2xl font-bold text-[#2F5887] mt-1">Resultado mensal</p>
                  </div>
                  <div className="w-12 h-12 rounded-xl bg-[#F5F7FA] flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-[#00C7A5]" />
                  </div>
                </div>

                <div className="space-y-4">
                  {[
                    { label: "Conformidade Fiscal", value: 98, color: "#00C7A5" },
                    { label: "Eficiência Tributária", value: 92, color: "#2F5887" },
                    { label: "Satisfação do Cliente", value: 100, color: "#00C7A5" },
                  ].map((b, i) => (
                    <div key={i}>
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-[#4B5563] font-medium">{b.label}</span>
                        <span className="font-bold text-[#111111]">{b.value}%</span>
                      </div>
                      <div className="h-2 bg-[#F5F7FA] rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full"
                          style={{ width: `${b.value}%`, backgroundColor: b.color }}
                        />
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-6 pt-6 border-t border-gray-100 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#2F5887] to-[#00C7A5]" />
                  <div>
                    <p className="text-sm font-semibold text-[#111111]">Equipe Especializada</p>
                    <p className="text-xs text-[#4B5563]">Atendimento dedicado ao seu negócio</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom fade */}
      <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-[#F5F7FA] to-transparent" aria-hidden="true" />
    </section>
  );
}
